import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { misc, businesses as bizApi, engagement, owner as ownerApi } from '../services/api';
import { Eye, Ticket, Award, Users, DollarSign, TrendingUp, Loader2, Building2, Gavel, Sparkles, Lock, Gift, Globe, Share2, MessageSquare, Star } from 'lucide-react';
import UpdatesBucket from '../components/UpdatesBucket';
import OwnerSliderFormPanel from '../components/OwnerSliderFormPanel';

export default function BusinessDashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [biz, setBiz] = useState([]);
  const [myBiz, setMyBiz] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [eng, setEng] = useState(null);
  useEffect(() => {
    if (!user?.isBusinessOwner) return;
    misc.businessStats().then(setS);
    bizApi.list().then(r => setBiz(r.slice(0, 5)));
    ownerApi.myBusinesses().then(mb => { setMyBiz(mb); if (mb.length) setSelectedBiz(mb[0].id); });
  }, [user]);
  useEffect(() => {
    if (selectedBiz) engagement.bizStats(selectedBiz).then(setEng).catch(() => setEng(null));
  }, [selectedBiz]);

  if (!user?.isBusinessOwner) {
    return (
      <div className="max-w-xl mx-auto pbb-card p-8 text-center">
        <Lock className="mx-auto text-green-400" size={28}/>
        <h1 className="mt-3 text-2xl font-bold">Business Owner Access Required</h1>
        <p className="text-gray-400 mt-2">The Business Dashboard is available to accounts registered as "Business Owner". Sign up again with the Business Owner group to unlock these tools.</p>
      </div>
    );
  }

  if (!s) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>;
  const max = Math.max(...(s.chart || [1]));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Business Dashboard</h1>
        <p className="text-gray-400 text-sm">Track engagement and manage your PBB presence.</p>
      </div>

      <UpdatesBucket limit={20} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Link to="/business/manage" className="pbb-card p-5 hover:-translate-y-0.5 transition-transform">
          <Building2 className="text-green-400 mb-2" size={22}/>
          <div className="font-semibold">Manage Listings</div>
          <div className="text-xs text-gray-400 mt-1">Add or edit the businesses you own in the directory.</div>
        </Link>
        <Link to="/business/coupons" className="pbb-card p-5 hover:-translate-y-0.5 transition-transform">
          <Ticket className="text-green-400 mb-2" size={22}/>
          <div className="font-semibold">My Coupons</div>
          <div className="text-xs text-gray-400 mt-1">Create promotions with discount, expiry and terms.</div>
        </Link>
        <Link to="/business/loyalty" className="pbb-card p-5 hover:-translate-y-0.5 transition-transform">
          <Gift className="text-green-400 mb-2" size={22}/>
          <div className="font-semibold">Loyalty Program</div>
          <div className="text-xs text-gray-400 mt-1">Set how points are earned and redeemed.</div>
        </Link>
        <Link to="/business/auctions" className="pbb-card p-5 hover:-translate-y-0.5 transition-transform">
          <Gavel className="text-green-400 mb-2" size={22}/>
          <div className="font-semibold">My Auction Items</div>
          <div className="text-xs text-gray-400 mt-1">List items to raise funds for the community.</div>
        </Link>
        <Link to="/business/upgrade" className="pbb-card p-5 hover:-translate-y-0.5 transition-transform">
          <Sparkles className="text-green-400 mb-2" size={22}/>
          <div className="font-semibold">Upgrade Plan</div>
          <div className="text-xs text-gray-400 mt-1">Unlock featured placement, analytics, and more.</div>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={Eye} label="Profile Views" value={s.views?.toLocaleString()} />
        <Stat icon={Ticket} label="Redemptions" value={s.redemptions?.toLocaleString()} />
        <Stat icon={Award} label="Points Issued" value={s.pointsIssued?.toLocaleString()} />
        <Stat icon={Users} label="Followers" value={s.followers?.toLocaleString()} />
        <Stat icon={DollarSign} label="Revenue" value={`$${s.revenue?.toLocaleString()}`} />
        <Stat icon={TrendingUp} label="Active Coupons" value={s.activeCoupons} />
      </div>

      {myBiz.length > 0 && (
        <div className="pbb-card p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="font-semibold">Engagement (live)</div>
            <select className="pbb-input" style={{ maxWidth: 260 }} value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)}>
              {myBiz.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          {eng ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <MiniStat icon={Eye} label="Views" value={eng.view?.count || 0}/>
              <MiniStat icon={Globe} label="Website Clicks" value={eng.website_click?.count || 0}/>
              <MiniStat icon={Share2} label="Social Clicks" value={eng.social_click?.count || 0}/>
              <MiniStat icon={MessageSquare} label="Reviews" value={eng.review?.count || 0}/>
              <MiniStat icon={Star} label="Ratings" value={eng.rating?.count || 0}/>
            </div>
          ) : <div className="text-sm text-gray-500">No engagement data yet.</div>}
        </div>
      )}

      <div className="pbb-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-semibold">Monthly Growth</div>
            <div className="text-xs text-gray-400">Profile views over the last 12 months</div>
          </div>
          <div className="text-green-400 text-sm">+18% MoM</div>
        </div>
        <div className="flex items-end gap-2 h-40">
          {(s.chart || []).map((v, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full rounded-t bg-green-500/70 hover:bg-green-500 transition-colors" style={{ height: `${(v / max) * 100}%` }}></div>
              <div className="text-[10px] text-gray-500">M{i+1}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="pbb-card p-5">
          <div className="font-semibold mb-3">Top-Performing Coupons</div>
          <ul className="divide-y divide-[#1f1f1f]">
            {biz.map(b => (
              <li key={b.id} className="flex items-center gap-3 py-3">
                <img src={b.image} alt="" className="w-10 h-10 rounded object-cover"/>
                <div className="flex-1">
                  <div className="text-sm font-medium">{b.coupon}</div>
                  <div className="text-xs text-gray-400">{b.name}</div>
                </div>
                <div className="text-sm text-green-400">{Math.floor((b.reviews||100) / 5)} redeems</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="pbb-card p-5">
          <div className="font-semibold mb-3">Recent Activity</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3"><Ticket className="text-green-400" size={16}/> <span>Coupon redeemed by <b>Marcus J.</b></span><span className="ml-auto text-xs text-gray-500">2m</span></li>
            <li className="flex items-center gap-3"><Users className="text-green-400" size={16}/> <span><b>Ayana W.</b> followed you</span><span className="ml-auto text-xs text-gray-500">18m</span></li>
            <li className="flex items-center gap-3"><Award className="text-green-400" size={16}/> <span>250 pts issued to <b>Devon B.</b></span><span className="ml-auto text-xs text-gray-500">1h</span></li>
            <li className="flex items-center gap-3"><DollarSign className="text-green-400" size={16}/> <span>New event ticket sale ($40)</span><span className="ml-auto text-xs text-gray-500">3h</span></li>
          </ul>
        </div>
      </div>
      <div className="pbb-card p-5">
        <OwnerSliderFormPanel />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="pbb-card p-4">
      <Icon className="text-green-400 mb-2" size={18}/>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-[#0d1f14] border border-[#153b23] p-3">
      <Icon className="text-green-400" size={16}/>
      <div className="text-xs text-gray-400 mt-1">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
